import React, { FunctionComponent, useEffect } from 'react'
import classNames from 'classnames'
import { Header } from '@lightspeed/design-system-react'
import { createSelector } from '@reduxjs/toolkit'
import { round } from 'vend-number'
import { getCurrencyFormatParts } from '@vend/utilities'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { RootState } from '../../../../state/store'
import { isReturn } from '../../../../functions/sale'
import { hasKlarnaPayments } from '../../../../functions/payment-types/klarna'
import { useAmountTenderedMessages } from './useAmountTenderedMessages'
import { usePaymentHeading } from './usePaymentHeading'

export interface AmountTenderedProps {
  amountTendered: string
  onChange: (amountTendered: string) => void
  setHasError: (hasError: boolean) => void
}

const SYSTEM_DECIMAL_SEPARATOR = '.'

const dataSelector = createSelector(
  [
    (state: RootState) => state.currentSale.originalSalePayments,
    (state: RootState) => state.currentSale.returnFor,
  ],
  (originalSalePayments, returnFor) => ({
    originalSalePayments,
    returnFor,
  })
)

const formatDisplayValue = (decimalSeparator: string, value: string) => {
  // We don't need to convert separators when `decimalSeparator` = `systemDecimalSeparator`.
  if (decimalSeparator === SYSTEM_DECIMAL_SEPARATOR) {
    return value
  }

  return Number.isFinite(Number(value))
    ? value.replace(
        new RegExp(`\\${SYSTEM_DECIMAL_SEPARATOR}`, 'g'),
        decimalSeparator
      )
    : value
}

const formatInputValue = (decimalSeparator: string, value: string) => {
  // We don't need to convert separators when `decimalSeparator` != `SYSTEM_DECIMAL_SEPARATOR`.
  if (decimalSeparator === SYSTEM_DECIMAL_SEPARATOR) {
    return value
  }

  return value.replace(
    new RegExp(`\\${decimalSeparator}`, 'g'),
    SYSTEM_DECIMAL_SEPARATOR
  )
}

export const AmountTendered: FunctionComponent<AmountTenderedProps> = ({
  amountTendered,
  onChange,
  setHasError,
}) => {
  const retailer = useRetailerSettingsManager().get()
  const activeOutlet = useRegisterManager().getActiveOutlet()
  const saleManager = useSaleManager()
  const heading = usePaymentHeading()
  const saleData = useAppSelector(dataSelector)
  const { error, message } = useAmountTenderedMessages(amountTendered, onChange)

  const decimalSeparator = getCurrencyFormatParts(
    retailer.culture,
    activeOutlet.currency,
    activeOutlet.currency_symbol
  ).decimal

  const shouldDisableAmountTendered =
    isReturn(saleData) &&
    hasKlarnaPayments({ payments: saleData.originalSalePayments })

  const onBlur = () => {
    if (!isNaN(Number(amountTendered))) {
      onChange(round(amountTendered, 2))
    }
  }

  useEffect(() => {
    setHasError(Boolean(error))
  }, [error, setHasError])

  return (
    <form name="amount-tendered" className="payment-form-container">
      <div
        className={classNames('payment-form vd-pb1', {
          'payment-form-with-cash-discounting': saleManager.hasCashDiscountingServiceFee(),
        })}
      >
        <Header type="hero" level="2" className="payment-header">
          {heading}
        </Header>
        <input
          value={formatDisplayValue(decimalSeparator, amountTendered)}
          onChange={e =>
            onChange(formatInputValue(decimalSeparator, e.target.value))
          }
          className={classNames('vd-input wr-input--amount-tendered', {
            'vd-input--error': error,
          })}
          disabled={shouldDisableAmountTendered}
          inputMode="decimal"
          aria-label="Amount tendered"
          autoComplete="off"
          onBlur={onBlur}
        />
      </div>
      <div className="payment-form-messages vd-text-supplementary">
        {error || message}
      </div>
    </form>
  )
}
